import React from "react";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import ActionButton from "@quest_editor/components/Buttons/ActionButton";
import BackButton from "@quest_editor/components/Buttons/BackButton";
import Breadcrumb from "@quest_editor/components/Breadcrumb";
import PromotionTypeChip from "@quest_editor/components/Chips/PromotionTypeChip";
import PromotionStatusChip from "@quest_editor/components/Chips/PromotionStatusChip";
import { useQuest, usePromotion, useUpdateQuest } from "@quest_editor/mocks";
import SurveySettingsSection, { SurveyFormValues } from "./SurveySettingsSection";

const QuestEditSurvey = () => {
  const { t } = useTranslation("questEditor");
  const navigate = useNavigate();
  const { id: questId } = useParams<{ id: string }>();

  const [formValues, setFormValues] = React.useState<SurveyFormValues>({
    preInstruction: "",
    qParam1: "",
    qParam2: "",
    qParam3: "",
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data: questData } = useQuest(questId);
  const quest = questData?.quest;

  const { data: promotionData } = usePromotion(quest?.promotionId);
  const promotion = promotionData?.promotion;

  const { updateQuest } = useUpdateQuest();
  const loadedQuestIdRef = React.useRef<string | undefined>();

  React.useEffect(() => {
    if (!quest || !questId) return;
    if (loadedQuestIdRef.current === questId) return;
    loadedQuestIdRef.current = questId;
    setFormValues({
      preInstruction: quest.surveyPreInstruction ?? "",
      qParam1: quest.qParam1Text ?? "",
      qParam2: quest.qParam2Text ?? "",
      qParam3: quest.qParam3Text ?? "",
    });
  }, [questId, quest]);

  const handleFormChange = (key: keyof SurveyFormValues, value: string) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    if (!questId) return;
    await updateQuest({ id: questId });
  };

  const handleSaveAndGotoSurveyEdit = async () => {
    if (!quest || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await save();
      navigate(`/quest_editor/survey/${quest.surveyId}/question`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveAndBack = async () => {
    if (!promotion || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await save();
      navigate(`/quest_editor/promotion/${promotion.id}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!quest || !promotion) {
    return <Typography>{t("common.label.not_found")}</Typography>;
  }

  return (
    <Stack direction="column" spacing={3} sx={{ flex: 1, minHeight: 0 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Stack direction="column" spacing={2} alignItems="flex-start">
          <Breadcrumb items={[{ label: promotion.name, to: `/quest_editor/promotion/${promotion.id}` }, { label: quest.name }]} />
          <Stack direction="row" spacing={1}>
            <PromotionTypeChip type={promotion.type} />
            <PromotionStatusChip status={promotion.status} />
          </Stack>
        </Stack>
      </Stack>

      <SurveySettingsSection
        formValues={formValues}
        onFormChange={handleFormChange}
        onSaveAndGotoSurveyEdit={handleSaveAndGotoSurveyEdit}
        isSubmitting={isSubmitting}
      />

      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        pt={2}
        sx={{ position: "relative" }}
      >
        <BackButton
          component={Link}
          to={`/quest_editor/quest/${quest.id}`}
          sx={{ position: "absolute", left: 0 }}
        >
          {t("quest_edit_survey.button.back")}
        </BackButton>
        <ActionButton
          size="small"
          onClick={handleSaveAndBack}
          disabled={isSubmitting}
        >
          {t("quest_edit_survey.button.save_and_back")}
        </ActionButton>
      </Stack>
    </Stack>
  );
};

export default QuestEditSurvey;
